import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AuthContext, AppSettingsContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Mail, Lock, AlertCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const { setUser } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for session expired message
  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      toast.error('Your session has expired. Please log in again.');
    }
  }, [searchParams]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      const userData = response.data.user;
      
      // Check if maintenance mode is on and user is not admin
      if (appSettings?.maintenance_mode && !userData.is_admin) {
        setError('Site is currently under maintenance. Only administrators can access the site.');
        toast.error('Site is under maintenance. Admin access only.');
        setLoading(false);
        return;
      }

      localStorage.setItem('session_token', response.data.session_token);
      // Store user in localStorage for analytics tracking (admin exclusion)
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      toast.success('Logged in successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex flex-col">
      <div className="flex-1 flex">
        {/* Left Side - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-slate-900/50 z-10"></div>
          <img
            src="https://images.unsplash.com/photo-1487466365202-1afdb86c764e"
            alt="Football Stadium"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 z-20 flex items-center justify-center p-6 lg:p-12">
            <div className="text-center w-full max-w-md">
              <a href="/" className="inline-block cursor-pointer">
                {appSettings.app_logo_url ? (
                  <img 
                    src={appSettings.app_logo_url} 
                    alt={appSettings.app_name} 
                    className="h-20 lg:h-24 max-w-[280px] lg:max-w-[300px] mx-auto mb-4 lg:mb-6 object-contain hover:opacity-80 transition-opacity"
                  />
                ) : (
                  <TrendingUp className="w-16 h-16 lg:w-20 lg:h-20 text-blue-400 mx-auto mb-4 lg:mb-6 hover:opacity-80 transition-opacity" />
                )}
              </a>
              <a href="/" className="inline-block cursor-pointer hover:opacity-80 transition-opacity">
                <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 lg:mb-4">{appSettings.app_name}</h1>
              </a>
              <p className="text-xl lg:text-2xl text-gray-200 mb-2">Track Your Bets</p>
              <p className="text-lg lg:text-xl text-gray-300">Master Your Game</p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Card className="glass-dark border-white/10">
            <CardHeader>
              <div className="flex items-center justify-center mb-4 lg:hidden">
                <a href="/" className="flex items-center cursor-pointer hover:opacity-80 transition-opacity">
                  {appSettings.app_logo_url ? (
                    <img 
                      src={appSettings.app_logo_url} 
                      alt={appSettings.app_name} 
                      className="h-14 max-w-[250px] object-contain"
                    />
                  ) : (
                    <>
                      <TrendingUp className="w-12 h-12 text-blue-400 mr-3" />
                      <CardTitle className="text-2xl sm:text-3xl text-white whitespace-nowrap">{appSettings.app_name}</CardTitle>
                    </>
                  )}
                </a>
              </div>
              <CardTitle className="text-xl sm:text-2xl text-white text-center">Welcome to {appSettings.app_name}</CardTitle>
              <p className="text-gray-400 text-center">Login to your account</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Google Login */}
              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-slate-900/50 text-gray-400">Or continue with email</span>
                </div>
              </div>

              {/* Email Login Form */}
              <form onSubmit={handleEmailLogin} className="space-y-4">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300">
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>

              <div className="text-center text-sm text-gray-400">
                Don&apos;t have an account?{' '}
                <Link to="/signup" className="text-blue-400 hover:text-blue-300 font-medium">
                  Sign up
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Login;
